/**
 * Member Transactions tab (DNKADM-0150) — this member's payment-transactions ledger
 * (the money-moved feed), pre-filtered by `contactId`. It REUSES the DNKADM-0149
 * `listPaymentTransactions` API + the reusable `PaymentTransactionsTable` rendered
 * `memberScoped` (the Contact column is dropped — every row is this member).
 *
 * The only member-scoped write action is the shared table's opt-in order **Refund /
 * Void** (DNKADM-0251), offered on a row that carries an `orderId` and confirmed
 * before it moves money; the tab passes `onReversed` so a reversal re-reads this
 * member's page (the full filter set + revenue rollup still live on the general
 * Transaction Ledger). Refund/Void is OWNER/ADMIN on the server, so a STAFF session gets
 * the table read-only (DNKADM-0465, the same gate as the ledger's `canReverse`). Loading /
 * empty ("No transactions for this member") / error+retry mirror `MemberActivityTab`; 1-indexed
 * UI paging (the 0149 API converts to the 0-indexed wire, DNKADM-0148). A "View in
 * Transaction ledger" link deep-links to the general ledger pre-filtered to this
 * member (`/transaction-ledger?tab=payments&contactId=<id>`), where the full filters
 * live. Enrichment (`contactName`, `reservationUuid`) is best-effort — a null value
 * is never an error (DNKAPI-0280/0281 forward contract).
 *
 * DNKADM-0430 P1-D: the page lives in the URL (`?payPage=`, 1-indexed, see
 * `memberDetailView.ts`) and the ledger link sits in the shared list toolbar. This tab
 * has no filters of its own (the full set is on the Transaction ledger) and, being
 * server paged, no sort in Phase 1.
 */
import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListToolbar } from '@/components/list/ListToolbar';
import { parsePage, toPageParam, useListUrlState } from '@/components/list/useListUrlState';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useStaffTier } from '@/auth/authHooks';
import { useCalendarZone } from '@/location/useCalendarZone';
import { listPaymentTransactions } from '@/api/paymentsTransactions';
import { PaymentTransactionsTable } from '@/features/transaction-ledger/PaymentTransactionsTable';
import { MEMBER_PAYMENTS_URL_DEFAULTS } from './memberDetailView';
import { ScreenError } from '@/components/ScreenError';

const PAGE_SIZE = 20;
const SKELETON_ROWS = 5;

export function MemberTransactionsTab({ contactId }: { contactId: string }) {
  const { values, set } = useListUrlState(MEMBER_PAYMENTS_URL_DEFAULTS);
  const page = parsePage(values.payPage);
  const canReverse = !useStaffTier();
  // DNKADM-0437 V-25: the When column renders in the selected location's venue zone, like the
  // main ledger, else the tenant's (DNKADM-0478). Read tolerantly (no throw outside a
  // provider): with neither known the zone is the device's.
  const venueTimeZone = useCalendarZone();

  const txns = useResource(
    useCallback(
      () => listPaymentTransactions({ contactId, page, size: PAGE_SIZE }),
      [contactId, page],
    ),
    [contactId, page],
  );

  const items = txns.data?.items ?? [];
  const totalPages = txns.data?.totalPages ?? null;

  return (
    <Box>
      <ListToolbar
        end={
          <Link
            component={RouterLink}
            to={`/transaction-ledger?tab=payments&contactId=${encodeURIComponent(contactId)}`}
            sx={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            View in Transaction ledger
            <Icon name="open_in_new" sx={{ fontSize: 16 }} />
          </Link>
        }
      />

      {txns.status === 'error' ? (
        <ScreenError
          variant="panel"
          error={txns.error}
          what="transactions"
          onRetry={txns.refetch}
          retryMessage="Something went wrong while loading this member’s transactions."
          sx={{ mt: 1 }}
        />
      ) : (
        <Paper elevation={1} sx={{ overflow: 'hidden' }}>
          {txns.status === 'loading' && <SkeletonBlock />}
          {txns.status === 'ready' && items.length === 0 && <EmptyState />}
          {/* A reversal re-reads this member's page — the server response is
              authoritative and the reversal adds a NEW refund row (DNKADM-0251). */}
          {txns.status === 'ready' && items.length > 0 && (
            <PaymentTransactionsTable
              rows={items}
              memberScoped
              {...(canReverse ? { onReversed: txns.refetch } : {})}
              timeZone={venueTimeZone}
            />
          )}
          {/* Past the first page the pager stays even when the page is empty (a stale
              link), so Previous is the way back. */}
          {txns.status === 'ready' && (items.length > 0 || page > 1) && (
            <Pagination
              page={page}
              totalPages={totalPages}
              itemCount={items.length}
              onPrev={() => set({ payPage: toPageParam(Math.max(1, page - 1)) })}
              onNext={() => set({ payPage: toPageParam(page + 1) })}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}

function SkeletonBlock() {
  return (
    <Box sx={{ px: 3, py: 2 }}>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <Skeleton key={`sk-${i}`} height={28} sx={{ my: 0.5 }} />
      ))}
    </Box>
  );
}

function EmptyState() {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="receipt_long" sx={{ fontSize: 44, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
        No transactions for this member
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
        Charges, membership dues and refunds for this member appear here, newest first.
      </Typography>
    </Box>
  );
}

function Pagination({
  page,
  totalPages,
  itemCount,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number | null;
  itemCount: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  // With a known page count, stop at the last page; otherwise offer Next until a
  // short (final) page arrives.
  const atLastPage = totalPages !== null ? page >= Math.max(1, totalPages) : itemCount < PAGE_SIZE;

  return (
    <Stack
      component="nav"
      aria-label="Pagination"
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${t.lineFaint}`,
      }}
    >
      <Typography variant="body2" aria-current="page">
        Page {page}
      </Typography>
      <IconAction label="Previous page" size="small" onClick={onPrev} disabled={page <= 1}>
        <Icon name="chevron_left" />
      </IconAction>
      <IconAction label="Next page" size="small" onClick={onNext} disabled={atLastPage}>
        <Icon name="chevron_right" />
      </IconAction>
    </Stack>
  );
}
